/**
 * Composant GED Statistiques
 * Visualisation des statistiques de la GED avec filtres par territoire, auteur, source
 */

import { DatePickerFr } from '../ui/DatePickerFr';
import React, { useState, useEffect } from 'react';
import { BarChart3, MapPin, Users, FolderOpen, Calendar, FileText, Filter, RefreshCw, TrendingUp, Globe } from 'lucide-react';
import { getToken } from '../../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GEDStatistiques = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtres
  const [selectedTerritoire, setSelectedTerritoire] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  
  const [territoires, setTerritoires] = useState([]);
  const [_sources, setSources] = useState({});

  const _getTokenLocal = () => getToken();

  // Charger les référentiels
  useEffect(() => {
    const loadReferentiels = async () => {
      try {
        const [terrRes, srcRes] = await Promise.all([
          fetch(`${API}/api/ged-unified/territoires`),
          fetch(`${API}/api/ged-unified/sources`)
        ]);
        
        if (terrRes.ok) {
          const data = await terrRes.json();
          setTerritoires(data.territoires || []);
        }
        if (srcRes.ok) {
          const data = await srcRes.json();
          setSources(data.sources || {});
        }
      } catch {
      }
    };
    
    loadReferentiels();
  }, []);

  // Charger les statistiques
  const loadStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (selectedTerritoire) params.append('territoire', selectedTerritoire);
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      
      const url = `${API}/api/ged-unified/statistiques${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des statistiques');
      }
      
      const data = await response.json();
      setStats(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, [selectedTerritoire, dateDebut, dateFin]);

  // Couleurs pour les graphiques
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 
    'bg-pink-500', 'bg-indigo-500', 'bg-red-500', 'bg-orange-500',
    'bg-teal-500', 'bg-cyan-500', 'bg-lime-500', 'bg-emerald-500'
  ];

  const getMaxCount = (items) => {
    if (!items || items.length === 0) return 1;
    return Math.max(...items.map(i => i.count));
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Chargement des statistiques...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <p className="font-medium">Erreur</p>
        <p>{error}</p>
        <button 
          onClick={loadStats}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ged-statistiques">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Statistiques GED</h2>
            <p className="text-gray-500">Traçabilité des documents par territoire, auteur et source</p>
          </div>
        </div>
        <button 
          onClick={loadStats}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-700">Filtres</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Territoire</label>
            <select 
              value={selectedTerritoire}
              onChange={(e) => setSelectedTerritoire(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              data-testid="filtre-territoire"
            >
              <option value="">Tous les territoires</option>
              {territoires.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
            <DatePickerFr
              value={dateDebut}
              onChange={setDateDebut}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
            <DatePickerFr
              value={dateFin}
              onChange={setDateFin}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <FileText className="h-10 w-10 opacity-80" />
            <TrendingUp className="h-5 w-5 opacity-60" />
          </div>
          <div className="mt-3">
            <p className="text-3xl font-bold">{stats?.total_documents || 0}</p>
            <p className="text-blue-100">Documents totaux</p>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <Globe className="h-10 w-10 opacity-80" />
          </div>
          <div className="mt-3">
            <p className="text-3xl font-bold">{stats?.par_territoire?.length || 0}</p>
            <p className="text-green-100">Territoires actifs</p>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <Users className="h-10 w-10 opacity-80" />
          </div>
          <div className="mt-3">
            <p className="text-3xl font-bold">{stats?.par_auteur?.length || 0}</p>
            <p className="text-purple-100">Contributeurs</p>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <FolderOpen className="h-10 w-10 opacity-80" />
          </div>
          <div className="mt-3">
            <p className="text-3xl font-bold">{stats?.par_source?.length || 0}</p>
            <p className="text-orange-100">Sources actives</p>
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Par Territoire */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">Documents par Territoire</h3>
          </div>
          <div className="space-y-3">
            {stats?.par_territoire?.length > 0 ? (
              stats.par_territoire.slice(0, 10).map((item, idx) => {
                const maxCount = getMaxCount(stats.par_territoire);
                const percentage = (item.count / maxCount) * 100;
                return (
                  <div key={item.territoire} className="flex items-center gap-3">
                    <div className="w-32 text-sm text-gray-600 truncate" title={item.territoire}>
                      {item.territoire}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div 
                        className={`h-full ${colors[idx % colors.length]} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-12 text-right font-medium text-gray-700">
                      {item.count}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">Aucune donnée</p>
            )}
          </div>
        </div>

        {/* Par Source */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-2 mb-4">
            <FolderOpen className="h-5 w-5 text-green-600" />
            <h3 className="font-semibold text-gray-800">Documents par Source</h3>
          </div>
          <div className="space-y-3">
            {stats?.par_source?.length > 0 ? (
              stats.par_source.slice(0, 10).map((item, idx) => {
                const maxCount = getMaxCount(stats.par_source);
                const percentage = (item.count / maxCount) * 100;
                return (
                  <div key={item.source} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-gray-600 truncate" title={item.source_label}>
                      {item.source_label}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div 
                        className={`h-full ${colors[(idx + 4) % colors.length]} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-12 text-right font-medium text-gray-700">
                      {item.count}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">Aucune donnée</p>
            )}
          </div>
        </div>

        {/* Par Auteur */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-purple-600" />
            <h3 className="font-semibold text-gray-800">Top Contributeurs</h3>
          </div>
          <div className="space-y-3">
            {stats?.par_auteur?.length > 0 ? (
              stats.par_auteur.slice(0, 10).map((item, idx) => {
                const maxCount = getMaxCount(stats.par_auteur);
                const percentage = (item.count / maxCount) * 100;
                return (
                  <div key={item.auteur_id} className="flex items-center gap-3">
                    <div className="w-40">
                      <div className="text-sm font-medium text-gray-700 truncate">{item.nom || 'Anonyme'}</div>
                      <div className="text-xs text-gray-400 truncate">{item.email}</div>
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div 
                        className={`h-full ${colors[(idx + 8) % colors.length]} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-12 text-right font-medium text-gray-700">
                      {item.count}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">Aucune donnée</p>
            )}
          </div>
        </div>

        {/* Par Jour */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-orange-600" />
            <h3 className="font-semibold text-gray-800">Activité Récente (30 derniers jours)</h3>
          </div>
          <div className="space-y-2">
            {stats?.par_jour?.length > 0 ? (
              stats.par_jour.slice(0, 15).map((item, _idx) => {
                const maxCount = getMaxCount(stats.par_jour);
                const percentage = (item.count / maxCount) * 100;
                const date = new Date(item.date);
                const formattedDate = date.toLocaleDateString('fr-FR', { 
                  weekday: 'short', 
                  day: 'numeric', 
                  month: 'short' 
                });
                return (
                  <div key={item.date} className="flex items-center gap-3">
                    <div className="w-28 text-sm text-gray-600">
                      {formattedDate}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-10 text-right font-medium text-gray-700">
                      {item.count}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-4">Aucune donnée</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GEDStatistiques;
